<template>
  <div>
    <div v-if="!loading">
      <h1 class="text-lg leading-tight font-medium pb-2 xl:text-2.5xl xl:pb-4">
        {{ $t('components.summary.title') }}
      </h1>

      <div v-if="errors.length" ref="error" class="w-full flex border border-gray-300 bg-white p-4 mt-2 mb-4">
        <div class="w-8 flex-shrink-0">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" v-bind:svg-inline="''" v-bind:class="'w-4.5 h-4.5'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M12.6 0H5.269L0 5.4v7.2L5.4 18h7.2l5.4-5.4V5.4zm1.665 12.189l-2.121 2.121-3.189-3.19-3.189 3.19-2.122-2.121L6.837 9l-3.19-3.189 2.122-2.122 3.189 3.19 3.189-3.19 2.121 2.122L11.08 9z" fill="#c00"/></svg>
        </div>
        <div class="pt-2px xl:pt-0">
          <p class="font-bold text-sm leading-none pb-2 xl:text-lg">
            {{ $t(`errors.type.${firstErrorKey}.title`, brandInfo) }}
          </p>
          <p class="text-sm xl:text-sm" v-html="$t(`errors.type.${firstErrorKey}.description`, brandInfo)" />
        </div>
      </div>

      <template>
        <div class="w-full mb-4">
          <div class="p-3 text-sm font-medium leading-tight border border-b-0 border-au_gray-100 xl:p-4 xl:text-lg bg-au_gray-100">
            {{ $t('components.summary.table.vouchers.title') }}
          </div>

          <div
            v-for="appliedVoucher in redemption.appliedVouchers"
            :key="appliedVoucher.id"
            class="text-sm xl:text-lg"
          >
            <div class="flex justify-between px-3 pt-3 pb-2 text-sm xl:px-4 xl:pt-4 xl:text-lg">
              <div class="mr-3 font-medium">
                {{ translateType(appliedVoucher.type) }}
                {{ $t('components.summary.table.vouchers.voucher_validity', { date: $d(new Date(appliedVoucher.validUntil)) }) }}
                - {{ appliedVoucher.code }}
              </div>
              <div class="flex items-center flex-shrink-0 font-medium font-numeric">
                {{ appliedVoucher.balanceInCents | lhFormatCurrency(currency, market) }}

                <button
                  class="flex-shrink-0 w-5 p-1 ml-2 hover:text-au_primary_hover"
                  :aria-label="$t('components.summary.flightVoucher.removeVoucher')"
                  @click.prevent="deleteVoucher(appliedVoucher)"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 14 18" v-bind:svg-inline="''" v-bind:class="'fill-current'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M1 16a2 2 0 002 2h8a2 2 0 002-2V4H1zM3 6h8v10H3zm7.5-5l-1-1h-5l-1 1H0v2h14V1z"/></svg>
                </button>
              </div>
            </div>
          </div>
        </div>

        <div v-if="shouldShowAddAnotherVoucher" class="mb-6">
          <div
            class="inline-flex items-center cursor-pointer text-au_blue-600 hover:text-au_primary_hover hover:underline"
            @click="showAddVoucherPopup = true"
          >
            <div class="flex-shrink-0 w-5 h-5 mr-2 cursor-pointer">
              <svg viewBox="0 0 120 120" xmlns="http://www.w3.org/2000/svg" v-bind:svg-inline="''" v-bind:class="'fill-current'" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path d="M96 71.973H71.973V96H24V48.027h24.027V24H96zm-30.014 18.04v-18.04h-17.96v-17.96h-18.04v36zm24.028-60.027h-36v36h36z"/></svg>
            </div>
            {{ $t('components.summary.link.add_more_voucher') }}
          </div>
        </div>

        <div class="w-full mb-8">
          <div class="p-3 text-sm font-medium leading-tight border border-b-0 border-au_gray-100 xl:p-4 xl:text-lg bg-au_gray-100">
            {{ $t('components.summary.table.ticket.title') }}
          </div>
          <div class="text-sm xl:text-lg">
            <div class="flex justify-between px-3 pt-3 pb-2 text-sm font-medium xl:px-4 xl:pt-4 xl:text-lg">
              <div class="mr-3">
                {{ $t('components.summary.table.ticket.ticketPrice') }}
              </div>
              <div class="flex items-center flex-shrink-0 font-medium font-numeric">
                {{ redemption.amount | lhFormatCurrency(currency, market) }}
              </div>
            </div>
          </div>
        </div>
      </template>

      <div class="w-full mb-8">
        <div class="p-3 text-sm font-medium leading-tight border border-b-0 border-au_gray-100 xl:p-4 xl:text-lg bg-au_gray-100">
          {{ $t('components.summary.finalSummary.title') }}
        </div>
        <div class="text-sm xl:text-lg">
          <div class="flex justify-between px-3 pt-3 pb-2 text-sm xl:px-4 xl:pt-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.flightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.vouchersBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>

          <div class="flex justify-between px-3 pt-2 pb-3 text-sm xl:px-4 xl:pb-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.totalDue | lhFormatCurrency(currency, market) }}
            </div>
          </div>

          <div class="flex justify-between p-3 text-sm font-medium text-white bg-au_gray-600 xl:p-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.remainingFlightVoucherBalance') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ remainingFlightVoucherBalance | lhFormatCurrency(currency, market) }}
            </div>
          </div>

          <div class="flex justify-between p-3 text-sm font-medium text-white bg-au_gray-600 xl:p-4 xl:text-lg">
            <div class="mr-3">
              {{ $t('components.summary.finalSummary.totalDue') }}
            </div>
            <div class="flex-shrink-0 font-numeric">
              {{ redemption.paymentSummary.fromCard | lhFormatCurrency(currency, market) }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="paymentRequired" class="flex xl:pt-6">
      <Button
        :disabled="loading"
        :loading="loading"
        class="w-full xl:max-w-xs"
        @click="continueToCCPayment"
      >
        {{ $t('components.summary.continueToCreditCardButton') }}
      </Button>
    </div>

    <div v-else class="flex xl:pt-6">
      <Button
        :disabled="loading"
        :loading="loading"
        class="w-full xl:max-w-xs"
        @click="confirmRedemption"
      >
        {{ $t('components.summary.submitButton') }}
      </Button>
    </div>

    <RemoveVoucherPopup
      v-if="voucherToDelete"
      :value="true"
      :voucher="voucherToDelete"
      @input="onRemoveVoucherInput"
    />
    <AddVoucherPopup v-model="showAddVoucherPopup" />
    <CancelRedemptionPopup v-model="showExitPopup" />
  </div>
</template>

<script lang="ts">
import Summary from '~psp-pages/base/redemption/summary.ts'

import Button from '~brand-components/austrian/button/button.vue'
import AddVoucherPopup from '~app/psp/austrian/add-voucher-popup.vue'
import CancelRedemptionPopup from '~app/psp/austrian/cancel-redemption-popup.vue'
import RemoveVoucherPopup from '~app/psp/austrian/remove-voucher-popup.vue'
import Payment from './payment.vue'

export default Summary.extend({
  components: {
    RemoveVoucherPopup,
    Payment,
    Button,
    AddVoucherPopup,
    CancelRedemptionPopup,
  },
})
</script>
